import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy
} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {catchError, debounceTime, map, startWith} from 'rxjs/operators';
import {DialogService, HttpRequestErrorHandler, UserSessionService, UnsavedChangeDetector} from '@rbaip/ui-component';
import {FormControl, Validators} from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {
  Asset,
  AssetInfo, AssetSaleFieldInfoTooltip,
  AssetSaleInfo, BudgetInfo,
  EquipmentCategory,
  EquipmentManufacturer,
  EquipmentModel, EquipmentType, EquipmentUsage, EXTRA_CONDITION, RBA_LIVE
} from '../../shared/domain/item';
import {ItemSummaryItemService} from '../../shared/asset/item-summary-item.service';
import {appMessage} from '../../shared/app.message';
import {AddAssetService} from '../../shared/asset/item-summary-add-new-asset.service';
import {PhotoGalleryUtils, GalleryPhoto} from '../../shared/photo-gallery';
import {permissionType, PermissionUtils} from "../../shared/permission";
import {AssetPackageDto} from "../../shared/domain/asset-package";
import { MatDialog } from '@angular/material/dialog';
import {AssetTypeChangeDialogComponent} from "../asset-type-change-dialog/asset-type-change-dialog.component";
import {getCurrencyCodeAndSymbol} from "../../shared/currencyUtil";

@Component({
  selector: 'lib-item-summary-asset-info',
  templateUrl: './asset-info.component.html',
  styleUrls: ['./asset-info.component.scss']
})
export class AssetInfoComponent extends UnsavedChangeDetector implements OnChanges, OnDestroy {

  @Input() itemId: number;
  @Input() assetInfo: AssetInfo;
  @Input() asset: Asset;
  @Input() assetSaleInfo: AssetSaleInfo;
  @Input() budgetInfo: BudgetInfo;
  @Input() saveAndCreateAnotherAsset: boolean;
  @Input() editEnabled: boolean;
  @Input() itemService: ItemSummaryItemService;
  @Output() afterSave = new EventEmitter();
  @Output() editModeChanged = new EventEmitter<boolean>();

  @ViewChild('typeControlAutoComplete', { read: MatAutocompleteTrigger }) matTypeAutoComplete: MatAutocompleteTrigger;
  @ViewChild('categoryControlAutoComplete', { read: MatAutocompleteTrigger }) matCategoryAutoComplete: MatAutocompleteTrigger;
  @ViewChild('modelControlAutoComplete', { read: MatAutocompleteTrigger }) matModelAutoComplete: MatAutocompleteTrigger;
  @ViewChild('makeControlAutoComplete', { read: MatAutocompleteTrigger }) matMakeAutoComplete: MatAutocompleteTrigger;
  @ViewChild('modelVirtualScrollViewport', { read: CdkVirtualScrollViewport }) modelVirtualScrollViewport: CdkVirtualScrollViewport;
  @ViewChild('makeVirtualScrollViewport', { read: CdkVirtualScrollViewport }) makeVirtualScrollViewport: CdkVirtualScrollViewport;
  @ViewChild('categoryVirtualScrollViewport', { read: CdkVirtualScrollViewport }) categoryVirtualScrollViewport: CdkVirtualScrollViewport;
  @ViewChild('typeVirtualScrollViewport', { read: CdkVirtualScrollViewport }) typeVirtualScrollViewport: CdkVirtualScrollViewport;
  @ViewChild('conditionControlAutoComplete', {read: MatAutocompleteTrigger }) conditionAutoComplete: MatAutocompleteTrigger;

  public readonly TITLE_NOT_REQUIRED: string = 'N';

  updatedAsset: Asset;
  originalAssetInfo: AssetInfo;
  inputedAssetInfo: AssetInfo;

  makeControl = new FormControl();
  modelControl = new FormControl();
  categoryControl = new FormControl();
  typeControl = new FormControl();
  conditionControl = new FormControl();
  postModelDescrControl = new FormControl(null, [Validators.maxLength(120)]);
  equipmentManufacturerList: EquipmentManufacturer[] = [];
  filteredManufacturerList: Observable<EquipmentManufacturer[]>;
  equipmentModelList: EquipmentModel[] = [];
  filteredEquipmentModelList: Observable<EquipmentModel[]>;
  filteredConditionList: Observable<{name: string, value: string}[]>;
  equipmentCategoryList: EquipmentCategory[] = [];
  filteredEquipmentCategoryList: Observable<EquipmentCategory[]>;
  equipmentTypeList: EquipmentType[] = [];
  filteredEquipmentTypeList: Observable<EquipmentType[]>;
  initialListsLoaded = false;
  autoSelectManufacturer = false;
  autoSelectModel = false;
  autoSelectCategory = false;
  autoSelectType = false;
  bAddTranslatedDesc = false;
  assetPackage: AssetPackageDto;
  selectedEquipmentCategory: EquipmentCategory;

  saveAttempted = false;
  public isFirstLoad = true;
  public isLoadingManufacturer = true;
  public isLoadingModelList = true;
  public isLoadingCategoryList = true;
  public isLoadingTypeList = true;
  public loadInitialCatalogs = false;
  public categorySelected = false;
  oldTypeId: number;
  oldCatId: number;
  oldMakeId: number;
  oldModelId: number;
  oldCat: string;
  oldType: string;
  oldMake: string;
  oldModel: string;
  oldDescription: string;
  validNumericValue: string;

  isReloadingManufacturerList = false;
  isReloadingModelList = false;
  isReloadingCategoryList = false;
  isReloadingTypeList = false;

  readonly MAX_NUMBER_OF_TITLES: number = 99;
  readonly MIN_NUMBER_OF_TITLES: number = 0;

  manufacturerSizeList : number = 0;
  modelSizeList : number = 0;
  categorySizeList : number = 0;
  typeSizeList : number = 0;

  public saveAssetInformationSubscription: Subscription;
  currentUserChangedSubscription: Subscription;
  public assetSaleInfoToolTips = AssetSaleFieldInfoTooltip;

  public conditionArray: {name: string, value: string }[] = [
    {value: '', name: 'Select Type'},
    {value: '1' , name: 'Excellent'},
    {value: '2', name: 'Very Good' },
    {value: '3', name: 'Good' },
    {value: '5', name: 'Not Viewed'},
    {value: '6', name: 'Fair' },
    {value: '7', name: 'Poor' }
  ];

  constructor(public addAssetService: AddAssetService,
              public dialogService: DialogService,
              public userSessionService: UserSessionService,
              private dialog: MatDialog) {
    super(appMessage.confirmSaveChanges.message);
    this.subscribeCurrentUserChanged();
    if ( this.assetInfo == null ) {
      this.assetInfo = new AssetInfo();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // refactor based on https://angular.io/api/core/OnChanges
    if(changes.saveAndCreateAnotherAsset && changes.saveAndCreateAnotherAsset.currentValue)
    {
      this.clearCategoryInput();
      this.clearMakeInput();
      this.clearModelInput();
      this.clearTypeInput();
      this.bAddTranslatedDesc = false;
      this.assetInfo.translatedDescr = '';
    }

    if (changes.assetInfo && changes.assetInfo.currentValue) {
      this.originalAssetInfo = changes.assetInfo.currentValue;
      this.init();
      this.subscribeToConditionChange();
      setTimeout(() => {
        if (this.assetInfo.editAssetInformation && !this.assetInfo.assetId && !this.initialListsLoaded) {
          this.initialListsLoaded = true;
          this.loadDropDownLists();
        } else if (this.assetInfo.editAssetInformation && this.assetInfo.assetId && !this.initialListsLoaded) {
          this.initialListsLoaded = true;
          this.loadPreSetDropDownLists();
        }
      }, 1000);
    }
  }

  public initializeAssetInfoComponent() {
    this.inputedAssetInfo = Object.assign(new AssetInfo(), JSON.parse(JSON.stringify(this.originalAssetInfo)));
    this.assetInfo = this.originalAssetInfo;
    this.oldTypeId = this.assetInfo.typeId;
    this.oldType = this.assetInfo.typeName;
    this.oldCatId = this.assetInfo.categoryId;
    this.oldCat = this.assetInfo.categoryName;
    this.oldMake = this.assetInfo.manufacturerName;
    this.oldMakeId = this.assetInfo.manufacturerId;
    this.oldModel = this.assetInfo.modelName;
    this.oldModelId = this.assetInfo.modelId;
    this.oldDescription = this.assetInfo.customDescrTxt;
    this.assetInfo.typeId = this.inputedAssetInfo.typeId;
    this.categoryControl.markAsTouched();
    this.typeControl.markAsTouched();
    // The condition from the backend is integer, but the front end define is as string, work around to not break other part of the application.
    this.conditionControl.setValue({name: this.assetInfo.conditionName, value: this.assetInfo.condition && this.assetInfo.condition.toString()});
    this.postModelDescrControl.setValue(this.assetInfo.postModelDescription);
  }

  public loadOriginalAssetValues() {
    if ( this.assetInfo.typeId && this.isFirstLoad ) {
      this.isFirstLoad = false;
      this.loadInitialCatalogs = true;
      this.assetInfo.categoryId = this.inputedAssetInfo.categoryId;
      this.assetInfo.customDescrTxt = this.inputedAssetInfo.customDescrTxt;
      this.assetInfo.usageTypeId = this.inputedAssetInfo.usageTypeId;
      this.makeControl.setValue(this.getAssetInfoManufacturerObjectValue(), {onlySelf: true, emitEvent: false});
      this.modelControl.setValue(this.getAssetInfoModelObjectValue(), {onlySelf: true, emitEvent: false});
      this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
      this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
    }
  }

  private initializeAssetValues(){
    this.assetInfo.numberOfTitles = 0;
  }

  init() {
    this.initializeAssetInfoComponent();
    if ( this.originalAssetInfo && this.originalAssetInfo.assetId ) {
      this.loadOriginalAssetValues();
    } else {
      this.initializeAssetValues();
    }
    this.showShootPrice();
    this.bAddTranslatedDesc = this.assetInfo
      && this.assetInfo.translatedDescr
      && this.assetInfo.translatedDescr.trim().length > 0;
    this.assetInfo.mergeCheckList = true;
  }

  ngOnDestroy() {
    if ( this.saveAssetInformationSubscription ) {
      this.saveAssetInformationSubscription.unsubscribe();
    }
    if ( this.currentUserChangedSubscription ) {
      this.currentUserChangedSubscription.unsubscribe();
    }
  }

  public subscribeManufacturerAutoCompleteChanges() {
    this.subscribeToSearchManufacturer();
    this.makeControl.valueChanges.subscribe(
      selectedMake => {
        const selectedValue: EquipmentManufacturer = ( typeof selectedMake === 'string' ) ? EquipmentManufacturer.constructEquipmentManufacturer(0, selectedMake) : selectedMake;
        const manufacturerId: number = (selectedValue) ? selectedValue.manufacturerId : 0;
        const manufacturerName: string = (selectedValue) ? selectedValue.manufacturerName : '';
        if ( !manufacturerId && this.assetInfo.manufacturerId && manufacturerId != this.assetInfo.manufacturerId ) {
          this.assetInfo.manufacturerId = undefined;
          this.assetInfo.manufacturerName = manufacturerName;
          this.cleanTypeAndCategoryFields();
          this.loadPreSetDropDownLists();
          this.generateDescription();
        } else if ( !( this.assetInfo.manufacturerName === manufacturerName ) ) {
          this.assetInfo.manufacturerName = manufacturerName;
          this.generateDescription();
        }
      });
    this.isLoadingManufacturer = false;
  }

  public subscribeToSearchManufacturer(): void {
    this.filteredManufacturerList = this.makeControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filterMakeList( ( typeof value === 'string' ) ? EquipmentManufacturer.constructEquipmentManufacturer(0, value) : value) ),
        debounceTime(500)
      );
  }

  public subscribeModelAutoCompleteChanges() {
    this.subscribeToSearchModel();
    this.modelControl.valueChanges.subscribe(
      selectedModel => {
        const selectedValue: EquipmentModel = ( typeof selectedModel === 'string' ) ? EquipmentModel.constructEquipmentModel(0, selectedModel) : selectedModel;
        const modelId: number = (selectedValue) ? selectedValue.modelId : 0;
        const modelName: string = (selectedValue) ? selectedValue.modelName : '';
        if ( !modelId && this.assetInfo.modelId && modelId != this.assetInfo.modelId ) {
          this.assetInfo.modelId = undefined;
          this.assetInfo.modelName = modelName;
          this.cleanTypeAndCategoryFields();
          this.loadPreSetDropDownLists();
          this.generateDescription();
        } else if ( !(this.assetInfo.modelName === modelName) ) {
          this.assetInfo.modelName = modelName;
          this.generateDescription();
        }
      });
    this.isLoadingModelList = false;
  }

  public subscribeToSearchModel(): void {
    this.filteredEquipmentModelList = this.modelControl.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filterModelList( ( typeof value === 'string' ) ? EquipmentModel.constructEquipmentModel(0, value) : value )),
        debounceTime(500)
      );
  }

  public subscribeCategoryAutoCompleteChanges() {
    this.subscribeToSearchCategory();
    this.categoryControl.valueChanges.subscribe(
      selectedCategory => {
        const selectedValue: EquipmentCategory = ( typeof selectedCategory === 'string' ) ? EquipmentCategory.constructEquipmentCategory(0, selectedCategory) : selectedCategory;
        const categoryId: number = ( selectedValue ) ? selectedValue.categoryId : 0 ;
        if ( !categoryId && this.assetInfo.categoryId && categoryId != this.assetInfo.categoryId ) {
          this.assetInfo.categoryId = undefined; this.assetInfo.typeId = undefined; this.assetInfo.manufacturerId = undefined; this.assetInfo.modelId = undefined;
          this.assetInfo.typeName = '';
          this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
          this.loadPreSetDropDownLists();
          this.generateDescription();
        }
      });
    this.isLoadingCategoryList = false;
  }

  public subscribeToSearchCategory(): void {
    this.filteredEquipmentCategoryList = this.categoryControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterCategoryList( ( typeof value === 'string' ) ? EquipmentCategory.constructEquipmentCategory(0, value) : value) ),
      debounceTime(500)
    );
  }
  public subscribeToConditionChange() {
    this.filteredConditionList = this.conditionControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterConditionList( ( typeof value === 'string' ) ? value : value.name) ),
      debounceTime(100)
    );

  }
  public clearCondition(event: any) {
    this.conditionControl.setValue('');
    this.assetInfo.condition = null;
  }
  public conditionFocusOut(event: any) {
    const input = this.conditionControl.value;
    if(input && typeof input === 'string') {
      this.clearCondition(null);
    }
  }
  public getConditionName(selectedCondition: {name: string, value: string}) {
    return selectedCondition
    && selectedCondition.value
    && selectedCondition.value.length > 0 ? selectedCondition.name : '';
  }

  public conditionSelected(event: MatAutocompleteSelectedEvent) {
    const selectedCondition: {name: string, value: string } = event.option.value;
    this.assetInfo.condition = selectedCondition.value;
    this.assetInfo.conditionName = selectedCondition.name;
  }

  public subscribeTypeAutoCompleteChanges(): void {
    this.subscribeToSearchType();
    this.typeControl.valueChanges.subscribe(
      selectedType => {
        const selectedValue: EquipmentType = ( typeof selectedType === 'string' ) ? EquipmentType.constructEquipmentType(0, selectedType) : selectedType;
        const typeId: number = (selectedValue) ? selectedValue.id : 0;
        if ( !typeId && this.assetInfo.typeId && typeId != this.assetInfo.typeId ) {
          this.assetInfo.typeId = undefined; this.assetInfo.manufacturerId = undefined; this.assetInfo.modelId = undefined; this.assetInfo.categoryId = undefined;
          this.assetInfo.categoryName = '';
          this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf:true, emitEvent: false});
          this.loadPreSetDropDownLists();
          this.generateDescription();
        }
      });
    this.isLoadingTypeList = false;
  }

  public subscribeToSearchType(): void {
    this.filteredEquipmentTypeList = this.typeControl.valueChanges.pipe(
      startWith(''),
      map(value =>
        this.filterTypeList( ( typeof value === 'string' ) ? EquipmentType.constructEquipmentType(0, value) : value )
      ),
      debounceTime(500)
    );
  }

  public changeAssetType() {
    if ( this.assetInfo.canSaveAssetInfo ) {
      if (this.oldTypeId !== this.assetInfo.typeId) {
        if (this.assetInfo.equipId > 0) {
          this.showAssetTypeChangeDialog();
        } else {
          const confirmCancelAssetCreation = appMessage.confirmAssetTypeChange;
          this.dialogService.showMessageDialog(confirmCancelAssetCreation)
            .afterClosed().subscribe(result => {
            if (result !== 'OK') {
              this.cancelAssetTypeChange();
            } else {
              this.generateDescription();
            }
            this.matTypeAutoComplete.closePanel();
          });
        }
      }
    }
  }
  private showAssetTypeChangeDialog(){
    this.itemService.isCheckListChanged(this.assetInfo.equipId, this.assetInfo.typeId).subscribe(result => {
      let checkListChange: boolean = result.data;
      if (checkListChange) {
        const dialogRef = this.dialog.open(AssetTypeChangeDialogComponent, {
          width: '500px',
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result.data.action !== 'OK') {
            this.cancelAssetTypeChange();
          } else {
            this.assetInfo.mergeCheckList = result.data.merge;
            this.generateDescription();
          }
          this.matTypeAutoComplete.closePanel();
        });
      }
    });
  }

  private cancelAssetTypeChange() {
    this.assetInfo.manufacturerId = this.oldMakeId;
    this.assetInfo.manufacturerName = this.oldMake;
    this.assetInfo.modelId = this.oldModelId;
    this.assetInfo.modelName = this.oldModel;
    this.assetInfo.categoryId = this.oldCatId;
    this.assetInfo.categoryName = this.oldCat;
    this.assetInfo.typeId = this.oldTypeId;
    this.assetInfo.typeName = this.oldType;
    this.makeControl.setValue(this.getAssetInfoManufacturerObjectValue(), {onlySelf: true, emitEvent: false});
    this.modelControl.setValue(this.getAssetInfoModelObjectValue(), {onlySelf: true, emitEvent: false});
    this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
    this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
    this.assetInfo.customDescrTxt = this.oldDescription;
    this.loadManufacturerList();
    this.loadModelList();
    this.loadCategoryList();
    this.loadSelectedCategory();
    this.loadTypeList();
  }

  public generateDescription() {
    this.checkAssetTypeMisc();
    if ( this.assetInfo.typeId !== undefined ){
        let typeName: string = this.assetInfo.typeName;
        if ( typeName && typeName.indexOf(':') > -1 ) {
          typeName = typeName.substr(0, typeName.indexOf(':')).replace(':', '');
          this.assetInfo.descriptionEditable = true;
        } else {
          this.assetInfo.descriptionEditable = false;
        }
        const year = this.assetInfo.year ? this.assetInfo.year : '';
        const make = this.assetInfo.manufacturerName ? this.assetInfo.manufacturerName : '';
        const model = this.assetInfo.modelName ? this.assetInfo.modelName : '';
        this.assetInfo.customDescrTxt = '';
        if ( year ) {
          this.assetInfo.customDescrTxt += year + ' ';
        }
        if ( make ) {
          this.assetInfo.customDescrTxt += make + ' ';
        }
        if ( model ) {
          this.assetInfo.customDescrTxt += model + ' ';
        }
        if ( this.assetInfo.postModelDescription ) {
          this.assetInfo.customDescrTxt += this.assetInfo.postModelDescription + ' ';
        }
        if ( typeName ) {
          this.assetInfo.customDescrTxt += typeName ;
        }
        if (this.assetInfo.unusedYn) {
          this.assetInfo.customDescrTxt += ' (' + EXTRA_CONDITION.unused + ')';
        }
        if (this.assetInfo.inoperableYn) {
          this.assetInfo.customDescrTxt += ' (' + EXTRA_CONDITION.inoperable + ')';
        }
        if (this.assetInfo.partsOnlyYn) {
          this.assetInfo.customDescrTxt += ' (' + EXTRA_CONDITION.partsOnly + ')';
        }
        if (this.assetInfo.glideredYn) {
          this.assetInfo.customDescrTxt += ' (' + EXTRA_CONDITION.glidered + ')';
        }
    } else {
      this.assetInfo.customDescrTxt = '';
    }
  }

  onYearChange(year) {
    if ( year ) {
      // refactor based on https://palantir.github.io/tslint/rules/radix/
      const intYear = parseInt(year, 10);
      const year5YearsLess = intYear - 5;
      this.assetSaleInfo.manufacturingYear = year;
      this.assetSaleInfo.manufacturingYear = year;
      this.assetSaleInfo.yearFrom = parseInt(this.assetSaleInfo.manufacturingYear);
      this.assetSaleInfo.yearTo = new Date().getFullYear();
      if (this.assetSaleInfo.yearFrom > this.assetSaleInfo.yearTo) {
        this.assetSaleInfo.yearFrom = this.assetSaleInfo.yearTo;

      }
    }
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if ( charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  isAlphanumric(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if ( (charCode >= 48 && charCode <= 57) || (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122) ) {
      return true;
    }
    return false;
  }

  /**
   * This method will prevent the decimal numbers
   * @param event
   */
  public preventDecimalValue(event, ngModel) {
    this.validNumericValue = (event.key === '.') ? ngModel : null;
    if (event.key === '.') {
      event.preventDefault();
    }
  }

  public loadManufacturerList(modelId?: number, categoryId?: number, typeId?: number) {
    const model: number = ( modelId ) ? modelId : 0;
    const category: number = ( categoryId ) ? categoryId : 0;
    const type: number = ( typeId ) ? typeId : 0;
    this.addAssetService.getManufacturerList(model, category, type).subscribe(manufacturers => {
      this.equipmentManufacturerList = manufacturers;
      if ( this.autoSelectManufacturer ){
        this.autoSelectManufacturer = false;
        this.selectUniqueManufacturerItemByDefault();
      }
      if ( this.equipmentManufacturerList.length === 0 ){
        this.addAssetService.getManufacturerList(0,0,0).subscribe(manufacturers => {
          this.equipmentManufacturerList = manufacturers;
          this.subscribeManufacturerAutoCompleteChanges();
        });
      }
      this.subscribeManufacturerAutoCompleteChanges();
    });
  }

  public loadModelList(makeId?: number, categoryId?: number, typeId?: number) {
    const make: number = ( makeId ) ? makeId : 0;
    const category: number = ( categoryId ) ? categoryId : 0;
    const type: number = ( typeId ) ? typeId : 0;
    this.addAssetService.getModelList(make, category, type).subscribe(models => {
      this.equipmentModelList = models;
      if ( this.autoSelectModel ){
        this.autoSelectModel = false;
        this.selectUniqueModelItemByDefault();
      }
      if (this.equipmentModelList.length === 0){
        this.addAssetService.getModelList(0,0,0).subscribe(models => {
          this.equipmentModelList = models;
          this.subscribeModelAutoCompleteChanges();
        });
      }
      this.subscribeModelAutoCompleteChanges();
    });
  }

  public loadTypeList(makeId?: number, modelId?: number, categoryId?: number) {
    const make: number = ( makeId ) ? makeId : 0;
    const model: number = ( modelId ) ? modelId : 0;
    const category: number = ( categoryId ) ? categoryId : 0;
    this.addAssetService.getTypesList(make, model, category).subscribe(types => {
      this.equipmentTypeList = types;
      this.checkAssetTypeMisc();
      if ( this.autoSelectType ){
        this.autoSelectType = false;
        this.selectUniqueTypeItemByDefault();
      }
      if ( makeId || modelId || this.assetInfo.manufacturerName || this.assetInfo.modelName ) {
        let preloadedFilteredList : EquipmentType[] = this.removeDuplicatedTypeItems(types, this.addAssetService.getPreLoadedTypeList());
        this.equipmentTypeList = [];
        this.equipmentTypeList = types.concat(preloadedFilteredList);
      }

      if ( this.equipmentTypeList.length === 0 ){
        this.addAssetService.getTypesList(0, 0, 0).subscribe(types => {
          this.equipmentTypeList = types;
          this.subscribeTypeAutoCompleteChanges();
        });
      }
      this.subscribeTypeAutoCompleteChanges();
    });
  }

  public loadCategoryList(makeId?: number, modelId?: number, typeId?: number) {
    const make: number = ( makeId ) ? makeId : 0;
    const model: number = ( modelId ) ? modelId : 0;
    const type: number = ( typeId ) ? typeId : 0;
    this.addAssetService.getCategoryList(make, model, type)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('loadCategoryList', null))
      )
      .subscribe(categories => {
      this.equipmentCategoryList = categories;
        if ( this.autoSelectCategory ){
          this.autoSelectCategory = false;
          this.selectUniqueCategoryItemByDefault();
        }
        if ( makeId || modelId || this.assetInfo.manufacturerName || this.assetInfo.modelName ) {
          let preloadedFilteredList : EquipmentCategory[] = this.removeDuplicatedCategoryItems(categories, this.addAssetService.getPreLoadedCategoryList());
          this.equipmentCategoryList = [];
          this.equipmentCategoryList = categories.concat(preloadedFilteredList);
        }

        if (this.equipmentCategoryList.length === 0 ) {
          this.addAssetService.getCategoryList(0,0,0).subscribe(categories => {
            this.equipmentCategoryList = categories;
            this.subscribeCategoryAutoCompleteChanges();
          });
        }
      this.subscribeCategoryAutoCompleteChanges();
    });
  }

  public loadSelectedCategory() {
    if ( this.assetInfo.categoryId && this.categorySelected ) {
      this.categorySelected = false;
      this.addAssetService.getCategoryById(this.assetInfo.categoryId)
        .pipe(
          catchError(HttpRequestErrorHandler.handleError('saveAssetInformation', null))
        )
        .subscribe(equipmentCategory => {
        this.selectedEquipmentCategory = EquipmentCategory.constructFromObjectJson(equipmentCategory);
        if ( this.isRequiredTitlePerCategory() ) {
          this.setDefaultNumberOfTitles();
        } else {
          this.resetNumberOfTitles();
        }
      });
    }
  }

  public isRequiredTitlePerCategory(): boolean {
    if ( this.selectedEquipmentCategory ) {
      return this.selectedEquipmentCategory.titleFlag != this.TITLE_NOT_REQUIRED;
    }
    return false;
  }

  public setDefaultNumberOfTitles() {
    this.assetInfo.numberOfTitles = 1;
  }

  public resetNumberOfTitles() {
    this.assetInfo.numberOfTitles = 0;
  }

  public convertToUpperCase(attrib: string, value: string) {
    if (value) {
      if (attrib === 'Serial Number') {
        this.assetInfo.serialNumber = value.toUpperCase();
        if (this.assetInfo.serialNumber && this.assetInfo.serialNumber.length > 0) {
          this.checkDuplicateSerialNumber(this.assetInfo.serialNumber);
        }
      } else if (attrib === 'License Plate') {
        this.assetInfo.licensePlate = value.toUpperCase();
      }
    }
  }

  private selectUniqueManufacturerItemByDefault(){
    if ( this.equipmentManufacturerList.length === 1 ){
      this.assetInfo.manufacturerId = this.equipmentManufacturerList[0].manufacturerId;
      this.assetInfo.manufacturerName = this.equipmentManufacturerList[0].manufacturerName;
      this.assetSaleInfo.make = this.equipmentManufacturerList[0].manufacturerName;
      this.makeControl.setValue( this.getAssetInfoManufacturerObjectValue() , {onlySelf: true, emitEvent: false});
      this.generateDescription();
    }
  }

  private selectUniqueTypeItemByDefault(){
    if ( this.equipmentTypeList.length === 1 ){
      this.assetInfo.typeId = this.equipmentTypeList[0].id;
      this.assetInfo.typeName = this.equipmentTypeList[0].name;
      this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
      this.generateDescription();
      if(this.assetInfo.equipId > 0) {
        this.showAssetTypeChangeDialog();
      }
    }
  }

  private selectUniqueCategoryItemByDefault(){
    if ( this.equipmentCategoryList.length === 1 ){
      this.assetInfo.categoryId = this.equipmentCategoryList[0].categoryId;
      this.assetInfo.categoryName = this.equipmentCategoryList[0].categoryName;
      this.categoryControl.setValue( this.getAssetInfoCategoryObjectValue() , {onlySelf: true, emitEvent: false});
      this.loadSelectedCategory();
      this.generateDescription();
    }
  }

  private selectUniqueModelItemByDefault(){
    if ( this.equipmentModelList.length === 1 ){
      this.assetInfo.modelId = this.equipmentModelList[0].modelId;
      this.assetInfo.modelName = this.equipmentModelList[0].modelName;
      this.modelControl.setValue( this.getAssetInfoModelObjectValue() , {onlySelf: true, emitEvent: false});
      this.generateDescription();
    }
  }

  checkUnUsed() {
    this.assetInfo.partsOnlyYn = false;
    this.assetInfo.inoperableYn = false;
    this.generateDescription();
  }

  checkInoperable() {
    this.assetInfo.partsOnlyYn = false;
    this.assetInfo.unusedYn = false;
    this.generateDescription();
  }

  checkPartsOnly() {
    this.assetInfo.unusedYn = false;
    this.assetInfo.inoperableYn = false;
    this.generateDescription();
  }

  checkGlidered() {
    this.generateDescription();
  }

  saveAssetInformation() {
    this.dialogService.showProgress();
    this.updatedAsset = this.assetInfo.setAssetFromAssetInfo(this.asset);
    this.updatedAsset.shootPrice = this.assetSaleInfo.shootPrice;
    if ( this.budgetInfo ){
      if ( this.budgetInfo.isEditable ){
        this.itemService.saveAssetBudget(this.itemId, this.budgetInfo.assetRnrBudget, this.budgetInfo.assetHaulingBudget).subscribe( response => {
          if ( response ){
            this.budgetInfo = response;
            this.updatedAsset.rnrBudget = this.budgetInfo.assetRnrBudget;
            this.updatedAsset.haulingBudget = this.budgetInfo.assetHaulingBudget;
            this.doSaveAssetInformation();
          }
        });
      } else {
        this.doSaveAssetInformation();
      }
    } else {
      this.doSaveAssetInformation();
    }
  }

  doSaveAssetInformation(): void {
      this.saveAssetInformationSubscription = this.itemService.saveAssetInformation(this.updatedAsset)
        .pipe(
          catchError(HttpRequestErrorHandler.handleError('saveAssetInformation', null))
        )
        .subscribe( response => {
          if (response.error) {
            this.dialogService.showMessage('Unable to save asset information. Please contact Tech Support.');
            this.dialogService.hideProgress();
          } else {
            this.toggleEditAssetFlag();
            this.dialogService.showMessage('Successfully saved Asset Information !');
            this.dialogService.hideProgress();
            this.asset = Asset.constructFromObjectJson(response);
            this.assetInfo = AssetInfo.getAssetInfoFromAsset(this.asset);
            this.afterSave.emit(this.assetInfo);
          }
        });
  }

  requestSave() {
    this.assetInfo.saveAttempted = true;
    const validationResult = this.assetInfo.validate();
    if (validationResult.isValid) {
      this.saveAssetInformation();
    } else {
      this.dialogService.showMessage(validationResult.errorMsg, 5000);
    }
  }

  toggleEditAssetFlag() {
    if ( this.assetInfo ) {
      this.assetInfo.editAssetInformation = ! this.assetInfo.editAssetInformation;
      this.broadcastEditModeChanged(this.assetInfo.editAssetInformation);
    }
  }

  cancel() {
    this.restoreOriginalValues();
    this.toggleEditAssetFlag();
    this.afterSave.emit();
  }

  restoreOriginalValues() {
    this.assetInfo.customDescrTxt = this.inputedAssetInfo.customDescrTxt;
    this.assetInfo.year = this.inputedAssetInfo.year;
    this.assetInfo.typeId = this.inputedAssetInfo.typeId;
    this.assetInfo.typeName = this.inputedAssetInfo.typeName;
    this.assetInfo.categoryId = this.inputedAssetInfo.categoryId;
    this.assetInfo.categoryName = this.inputedAssetInfo.categoryName;
    this.assetInfo.modelId = this.inputedAssetInfo.modelId;
    this.assetInfo.modelName = this.inputedAssetInfo.modelName;
    this.assetInfo.manufacturerId = this.inputedAssetInfo.manufacturerId;
    this.assetInfo.manufacturerName = this.inputedAssetInfo.manufacturerName;
    this.assetInfo.serialNumber = this.inputedAssetInfo.serialNumber;
    this.assetInfo.licensePlate = this.inputedAssetInfo.licensePlate;
    this.assetInfo.usageQuantity = this.inputedAssetInfo.usageQuantity;
    this.assetInfo.usageTypeId = this.inputedAssetInfo.usageTypeId;
    this.assetInfo.condition = this.inputedAssetInfo.condition;
    this.assetInfo.unusedYn = this.inputedAssetInfo.unusedYn;
    this.assetInfo.inoperableYn = this.inputedAssetInfo.inoperableYn;
    this.assetInfo.partsOnlyYn = this.inputedAssetInfo.partsOnlyYn;
    this.assetInfo.glideredYn = this.inputedAssetInfo.glideredYn;
    this.assetInfo.sellerTrackingCode = this.inputedAssetInfo.sellerTrackingCode;
    this.assetInfo.sellerContractNumber = this.inputedAssetInfo.sellerContractNumber;
    this.assetInfo.numberOfTitles = this.inputedAssetInfo.numberOfTitles;
    this.assetInfo.postModelDescription = this.inputedAssetInfo.postModelDescription;
    this.assetInfo.translatedDescr = this.inputedAssetInfo.translatedDescr;
    this.assetInfo.conditionName = this.inputedAssetInfo.conditionName;
    this.conditionControl.setValue({name: this.inputedAssetInfo.conditionName, value: this.inputedAssetInfo.condition && this.inputedAssetInfo.condition.toString()});
    this.makeControl.setValue(this.getAssetInfoManufacturerObjectValue(), {onlySelf: true, emitEvent: false});
    this.modelControl.setValue(this.getAssetInfoModelObjectValue(), {onlySelf: true, emitEvent: false});
    this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
    this.typeControl.setValue( this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
  }

  showPhotoGallerySerialNumber(): void {
    if (this.assetInfo.serialNumberPhotoUrls) {
      const photo = this.constructPhotoObject(this.assetInfo.serialNumberPhotoUrls, 'Serial');
      PhotoGalleryUtils.showPhotoGallery(photo, 0);
    }
  }

  showPhotoGalleryMeterReading(): void {
    if (this.assetInfo.meterReadingPhotoUrls) {
      const photo = this.constructPhotoObject(this.assetInfo.meterReadingPhotoUrls, 'Odometer');
      PhotoGalleryUtils.showPhotoGallery(photo, 0);
    }
  }

  constructPhotoObject(photoUrls: string[], typeName: string): GalleryPhoto[] {
    const imageList = [];
    photoUrls.forEach( photoUrl => {
      imageList.push({
        typeName,
        imageUrls: {
          small: photoUrl,
          big: photoUrl
        }
      });
    });
    return imageList;

  }

  checkDuplicateSerialNumber(serialNumber: string) {
    this.itemService.duplicateSerialNumber(serialNumber, this.itemId, this.asset.assetControlGroupId)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('checkDuplicateSerialNumber', null))
      )
      .subscribe( isDuplicate => {
      this.assetInfo.duplicateSerialNumber = isDuplicate;
    });
  }

  checkDuplicateSellerTrackingCode(sellerTrackingCode: string) {
    this.itemService.duplicateSellerTracking(sellerTrackingCode, this.itemId, this.asset.assetControlGroupId)
      .pipe(
        catchError(HttpRequestErrorHandler.handleError('checkDuplicateSellerTrackingCode', null))
      )
      .subscribe( isDuplicate => {
      this.assetInfo.duplicateSellerTrackingCode = isDuplicate;
    });
  }

  isSerialNumberInvalid(value) {
    return value && value.length > 17;
  }

  public setManufacturerName( event: MatAutocompleteSelectedEvent ): void {
    const manufacturerSelected: EquipmentManufacturer = event.option.value;
    if ( this.assetInfo.manufacturerId != +manufacturerSelected.manufacturerId ) {
      this.assetInfo.manufacturerId = +manufacturerSelected.manufacturerId;
      this.assetInfo.manufacturerName = manufacturerSelected.manufacturerName;
      this.assetSaleInfo.make = manufacturerSelected.manufacturerName;
      this.syncModelList(true);
      this.syncCategoryList();
      this.syncTypeList();
      this.categorySelected = true;
      this.generateDescription();
    }
  }

  getManufacturerName(manufacturerSelected?: any): string {
    return (manufacturerSelected) ? manufacturerSelected.manufacturerName : '';
  }

  public setModelName( event: MatAutocompleteSelectedEvent ): void {
    const modelSelected: EquipmentModel = event.option.value;
    if ( this.assetInfo.modelId !== +modelSelected.modelId) {
      this.assetInfo.modelId = +modelSelected.modelId;
      this.assetInfo.modelName = modelSelected.modelName;
      this.assetSaleInfo.model = modelSelected.modelName;
      this.syncManufacturerList();
      this.syncCategoryList();
      this.syncTypeList();
      this.categorySelected = true;
      this.generateDescription();
    }
  }

  getModelName( modelSelected ?: any ): string {
      return (modelSelected) ? modelSelected.modelName : '';
  }

  public setMatOptionCategoryId( event: MatAutocompleteSelectedEvent ): void {
    const categorySelected: EquipmentCategory = event.option.value;
    let previousCategoryId : number = this.assetInfo.categoryId;
    if ( this.assetInfo.categoryId != categorySelected.categoryId) {
      this.assetInfo.categoryId = categorySelected.categoryId;
      this.assetInfo.categoryName = categorySelected.categoryName;
      this.categorySelected = true;

      if ( previousCategoryId ){
        if ( this.assetInfo.manufacturerId ){
          this.assetInfo.manufacturerId = undefined;
        }
        if ( this.assetInfo.modelId ){
          this.assetInfo.modelId = undefined;
        }
        if ( this.assetInfo.typeId ){
          this.assetInfo.typeId = undefined;
          this.assetInfo.typeName = '';
          this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
        }
      }
      this.loadSelectedCategory();
      this.syncManufacturerList(true);
      this.syncModelList(true);
      this.syncTypeList();
      this.categorySelected = true;
      this.generateDescription();
    }
  }

  getCategoryName( categorySelected ?: any): string {
      return (categorySelected) ? categorySelected.categoryName : '';
  }

  public setMatOptionTypeId( event: MatAutocompleteSelectedEvent ) {
    const typeSelected: EquipmentType = event.option.value;
    let previousTypeId : number = this.assetInfo.typeId;
    if ( this.assetInfo.typeId != typeSelected.id ) {
      this.assetInfo.typeId = typeSelected.id;
      this.assetInfo.typeName = typeSelected.name;
      if ( typeSelected.name.startsWith(':') ) {
        this.assetInfo.isAssetTypeMisc = true;
      }

      if ( previousTypeId ) {
        if ( this.assetInfo.manufacturerId ){
          this.assetInfo.manufacturerId = undefined;
        }
        if ( this.assetInfo.modelId ){
          this.assetInfo.modelId = undefined;
        }
        if ( this.assetInfo.categoryId ){
          this.assetInfo.categoryId = undefined;
          this.assetInfo.categoryName = '';
          this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf:true, emitEvent: false});
        }
      }

      this.changeAssetType();
      this.syncManufacturerList(true);
      this.syncModelList(true);
      this.syncCategoryList();
      this.categorySelected = true;
      this.generateDescription();
    }
  }

  getTypeName( typeSelected?: any): string {
      return (typeSelected) ?  typeSelected.name : '';
  }

  public filterConditionList(value: string): {name: string, value: string} [] {
    const filteredValues = [];
    const inputValue = value ? value.trim() : '';
    for (const condition of this.conditionArray) {
      if (condition.name.toLocaleLowerCase().startsWith(inputValue.toLowerCase())) {
        filteredValues.push(condition);
      }
    }
    return filteredValues;
  }

  public filterMakeList(value: any): EquipmentManufacturer[] {
    const keyword: string = (value) ? value.manufacturerName : '';
    if ( this.makeVirtualScrollViewport ) {
      this.makeVirtualScrollViewport.scrollToIndex(0);
    }
    return this.sortFilterManufacturerValues( this.equipmentManufacturerList, this.getActualFilterValue(keyword) );
  }

  public filterModelList(value: any): EquipmentModel[] {
    const keyword: string = (value) ? value.modelName : '';
    if ( this.modelVirtualScrollViewport ) {
      this.modelVirtualScrollViewport.scrollToIndex(0);
    }
    return this.sortFilterModelValues(this.equipmentModelList, this.getActualFilterValue(keyword));
  }

  public getActualFilterValue(value: any): string {
    let filterValue = '';
    if ( value ) {
      filterValue = value;
    }
    return filterValue;
  }

  public filterCategoryList(value: any): EquipmentCategory[] {
    const keyword: string = ( value ) ? value.categoryName : '';
    if ( this.categoryVirtualScrollViewport ) {
      this.categoryVirtualScrollViewport.scrollToIndex(0);
    }
    return this.sortFilterEquipmentCategoryValues(this.equipmentCategoryList, this.getFilterValue(keyword));
  }

  public filterTypeList(value: any): EquipmentType[] {
    const keyword: string = (value) ? value.name : '';
    if ( this.typeVirtualScrollViewport ) {
      this.typeVirtualScrollViewport.scrollToIndex(0);
    }
    return this.sortFilterEquipmentTypeValues(this.equipmentTypeList, this.getFilterValue(keyword));
  }

  public sortFilterManufacturerValues(originList: EquipmentManufacturer[], startWithValue: string): EquipmentManufacturer [] {
    const startWithValueArray: EquipmentManufacturer[] = [];
    const notStartWithValueArray: EquipmentManufacturer[] = [];
    const defaultValueArray: EquipmentManufacturer[] = [];
    for ( const item of originList) {

      if ( startWithValue && item.manufacturerName.toLowerCase().startsWith(startWithValue.toLowerCase())) {
        startWithValueArray.push(item);
      } else if ( startWithValue && ( this.includesAnyKeyword(item.manufacturerName, startWithValue) && !item.manufacturerName.toLowerCase().startsWith(startWithValue.toLowerCase()) ) ) {
        notStartWithValueArray.push(item);
      } else {
        defaultValueArray.push(item);
      }
    }
    if (startWithValueArray.length === 0 && notStartWithValueArray.length === 0) {
      const customizedMake = EquipmentManufacturer.constructEquipmentManufacturer(0, startWithValue);
      defaultValueArray.unshift(customizedMake);
    }
    const response : EquipmentManufacturer[] = Object.assign( [] , startWithValueArray.concat(notStartWithValueArray , defaultValueArray) );
    this.manufacturerSizeList = response.length;
    if ( this.makeVirtualScrollViewport ) {
      this.makeVirtualScrollViewport.scrollToIndex(0);
    }
    return response;
  }

  public sortFilterModelValues(originList: EquipmentModel[], startWithValue: string): EquipmentModel[] {
    const startWithValueArray: EquipmentModel[] = [];
    const notStartWithValueArray: EquipmentModel[] = [];
    const defaultValueArray: EquipmentModel[] = [];
    for ( const item of originList) {
      if ( startWithValue && item.modelName.toLowerCase().startsWith(startWithValue.toLowerCase())) {
        startWithValueArray.push(item);
      } else if ( startWithValue && ( this.includesAnyKeyword(item.modelName, startWithValue) && !item.modelName.toLowerCase().startsWith(startWithValue.toLowerCase()) ) ) {
        notStartWithValueArray.push(item);
      } else {
        defaultValueArray.push(item);
      }
    }
    if (startWithValueArray.length === 0 && notStartWithValueArray.length === 0) {
      const customizedModel = EquipmentModel.constructEquipmentModel(0, startWithValue);
      customizedModel.typeName = startWithValue && startWithValue.trim().length > 0 ? 'Other' : 'Please select the model';
      defaultValueArray.unshift(customizedModel);
    }
    const response: EquipmentModel[] = Object.assign([], startWithValueArray.concat(notStartWithValueArray , defaultValueArray));
    this.modelSizeList = response.length;
    if ( this.modelVirtualScrollViewport ){
      this.modelVirtualScrollViewport.scrollToIndex(0);
    }
    return response;
  }

  public sortFilterEquipmentCategoryValues(originList: EquipmentCategory[], startWithValue: string): EquipmentCategory[] {
    const startWithValueArray: EquipmentCategory[] = [];
    const notStartWithValueArray: EquipmentCategory[] = [];
    const defaultValueArray: EquipmentCategory[] = [];
    for ( const item of originList) {
      if ( startWithValue && item.categoryName.toLowerCase().startsWith(startWithValue)) {
        startWithValueArray.push(item);
      } else if ( startWithValue && ( this.includesAnyKeyword(item.categoryName, startWithValue) && !item.categoryName.toLowerCase().startsWith(startWithValue) ) ) {
        notStartWithValueArray.push(item);
      } else {
        defaultValueArray.push(item);
      }
    }
    const response: EquipmentCategory[] = Object.assign( [] , startWithValueArray.concat(notStartWithValueArray,defaultValueArray));
    this.categorySizeList = response.length;
    if ( this.categoryVirtualScrollViewport ){
      this.categoryVirtualScrollViewport.scrollToIndex(0);
    }
    return response;
  }

  public sortFilterEquipmentTypeValues(originList: EquipmentType[], startWithValue: string): EquipmentType[] {
    const startWithValueArray: EquipmentType[] = [];
    const notStartWithValueArray: EquipmentType[] = [];
    const defaultValueArray: EquipmentType[] = [];
    for ( const item of originList) {
      if ( startWithValue && item.name.toLowerCase().startsWith(startWithValue)) {
        startWithValueArray.push(item);
      } else if ( startWithValue && ( item.name.toLowerCase().includes(startWithValue) && !item.name.toLowerCase().startsWith(startWithValue) ) ) {
        notStartWithValueArray.push(item);
      } else {
        defaultValueArray.push(item);
      }
    }
    const response : EquipmentType[] = Object.assign( [] , startWithValueArray.concat(notStartWithValueArray,defaultValueArray));
    this.typeSizeList = response.length;
    if ( this.typeVirtualScrollViewport ){
      this.typeVirtualScrollViewport.scrollToIndex(0);
    }
    return response;
  }

  public isLoadingLists(): boolean {
    return this.isLoadingManufacturer && this.isLoadingModelList && this.isLoadingCategoryList && this.isLoadingTypeList;
  }

  public getFilterValue(value: any): string {
    let filterValue = '';
    if ( value ) {
      filterValue = value.toLowerCase();
    }
    return filterValue;
  }

  public includesAnyKeyword(source: string, keyword: string): boolean {
    if ( source.toLowerCase().includes(keyword) ) { return true; }
    const keywordArray: string[] = keyword.split(' ');
    if ( keywordArray.length === 1 ) { return false; }
    for ( const keyword of keywordArray) {
      if ( source.toLowerCase().includes(keyword) ) {
        return true;
      }
    }
    return false;
  }

  public openTypeAutocomplete(): void {
    setTimeout(() => {
      if ( this.matModelAutoComplete.panelOpen ) this.matModelAutoComplete.closePanel();
      if ( this.matCategoryAutoComplete.panelOpen ) this.matCategoryAutoComplete.closePanel();
      if ( this.matMakeAutoComplete.panelOpen ) this.matMakeAutoComplete.closePanel();
      this.matTypeAutoComplete.openPanel();
      if ( this.typeVirtualScrollViewport ) {
        this.typeVirtualScrollViewport.setTotalContentSize(this.typeSizeList);
        this.typeVirtualScrollViewport.checkViewportSize();
        this.typeVirtualScrollViewport.scrollToIndex(0);
      }
    });
  }

  public closeTypeAutocomplete(): void {
    setTimeout(() => this.matTypeAutoComplete.closePanel());
  }

  public clearTypeInput(): void {
    this.assetInfo.typeId = undefined;
    this.assetInfo.typeName = '';
    this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
    this.cleanTypeAndCategoryFields();
    this.loadPreSetDropDownLists();
    this.generateDescription();
  }

  public reloadDefaultTypeList(): void {
    this.isReloadingTypeList = true;
    this.equipmentTypeList = this.addAssetService.getPreLoadedTypeList();
    this.subscribeToSearchType();
    this.openTypeAutocomplete();
    setTimeout(() => {
      this.isReloadingTypeList = false;
    }, 3000);
  }

  public openCategoryAutocomplete(): void {
    setTimeout(() => {
      if ( this.matModelAutoComplete.panelOpen ) this.matModelAutoComplete.closePanel();
      if ( this.matTypeAutoComplete.panelOpen ) this.matTypeAutoComplete.closePanel();
      if ( this.matMakeAutoComplete.panelOpen ) this.matMakeAutoComplete.closePanel();
      this.matCategoryAutoComplete.openPanel();
      if ( this.categoryVirtualScrollViewport ) {
        this.categoryVirtualScrollViewport.setTotalContentSize(this.categorySizeList);
        this.categoryVirtualScrollViewport.checkViewportSize();
        this.categoryVirtualScrollViewport.scrollToIndex(0);
      }
    });
  }

  public closeCategoryAutocomplete(): void {
    setTimeout(() => this.matCategoryAutoComplete.closePanel());
  }

  public clearCategoryInput(): void {
    this.assetInfo.categoryId = undefined;
    this.assetInfo.categoryName = '';
    this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
    this.loadPreSetDropDownLists();
    this.generateDescription();
  }

  public reloadDefaultCategoryList(): void {
    this.isReloadingCategoryList = true;
    this.equipmentCategoryList = this.addAssetService.getPreLoadedCategoryList();
    this.subscribeToSearchCategory();
    this.openCategoryAutocomplete();
    setTimeout(() => {
      this.isReloadingCategoryList = false;
    }, 3000);
  }

  public openModelAutocomplete(): void {
    setTimeout(() => {
      if ( this.matCategoryAutoComplete.panelOpen ) this.matCategoryAutoComplete.closePanel();
      if ( this.matTypeAutoComplete.panelOpen ) this.matTypeAutoComplete.closePanel();
      if ( this.matMakeAutoComplete.panelOpen ) this.matMakeAutoComplete.closePanel();
      this.matModelAutoComplete.openPanel();
        if ( this.modelVirtualScrollViewport ) {
          this.modelVirtualScrollViewport.setTotalContentSize(this.modelSizeList);
          this.modelVirtualScrollViewport.checkViewportSize();
          this.modelVirtualScrollViewport.scrollToIndex(0);
        }
    });
  }

  public closeModelAutocomplete(): void {
    setTimeout(() => this.matModelAutoComplete.closePanel());
  }

  public toggleConditionAutocomplete(isOpened: boolean): void {
    setTimeout(() => {
      if (isOpened) {
        this.conditionAutoComplete.closePanel();
      }  else {
        this.conditionAutoComplete.openPanel();
      }
    });
  }

  public clearModelInput(): void {
    this.assetInfo.modelId = undefined;
    this.assetInfo.modelName = '';
    this.modelControl.setValue(this.getAssetInfoModelObjectValue(), {onlySelf: true, emitEvent: false});
    this.cleanTypeAndCategoryFields();
    this.loadPreSetDropDownLists();
    this.generateDescription();
  }

  public reloadDefaultModelList(): void {
    this.isReloadingModelList = true;
    this.equipmentModelList = this.addAssetService.getPreLoadedModelList();
    this.subscribeToSearchModel();
    this.openModelAutocomplete();
    setTimeout(() => {
      this.isReloadingModelList = false;
    }, 3000);
  }

  public openMakeAutocomplete(): void {
    setTimeout(() => {
      if  ( this.matModelAutoComplete.panelOpen )  this.matModelAutoComplete.closePanel();
      if  ( this.matCategoryAutoComplete.panelOpen ) this.matCategoryAutoComplete.closePanel();
      if  ( this.matTypeAutoComplete.panelOpen ) this.matTypeAutoComplete.closePanel();
      this.matMakeAutoComplete.openPanel();
      if ( this.makeVirtualScrollViewport ) {
        this.makeVirtualScrollViewport.setTotalContentSize( this.manufacturerSizeList);
        this.makeVirtualScrollViewport.checkViewportSize();
        this.makeVirtualScrollViewport.scrollToIndex(0);
      }
    });
  }

  public closeMakeAutocomplete(): void {
    setTimeout(() => this.matMakeAutoComplete.closePanel());
  }

  public clearMakeInput(): void {
    this.assetInfo.manufacturerId = undefined;
    this.assetInfo.manufacturerName = '';
    this.makeControl.setValue(this.getAssetInfoManufacturerObjectValue(), {onlySelf: true, emitEvent: false});
    this.cleanTypeAndCategoryFields();
    this.loadPreSetDropDownLists();
    this.generateDescription();
  }

  public reloadDefaultMakeList(): void {
    this.isReloadingManufacturerList = true;
    this.equipmentManufacturerList = this.addAssetService.getPreLoadedManufacturerList();
    this.subscribeToSearchManufacturer();
    this.openMakeAutocomplete();
    setTimeout(() => {
      this.isReloadingManufacturerList = false;
    }, 3000);
  }

  decreaseNumberOfTitles() {
    if (this.assetInfo.numberOfTitles > this.MIN_NUMBER_OF_TITLES) {
      this.assetInfo.numberOfTitles --;
    }
  }

  increaseNumberOfTitles() {
    if (this.assetInfo.numberOfTitles < this.MAX_NUMBER_OF_TITLES) {
      this.assetInfo.numberOfTitles ++;
    }
  }

  public preventInput(event) {
    const value = this.assetInfo.numberOfTitles;
    if (value < 0) {
      event.preventDefault();
      this.assetInfo.numberOfTitles = 0;
    } else if (value > this.MAX_NUMBER_OF_TITLES) {
      event.preventDefault();
      this.assetInfo.numberOfTitles = parseInt(value.toString().substring(0, 2));
    }
  }

  public subscribeCurrentUserChanged() {
    this.currentUserChangedSubscription = this.userSessionService.userChanged
      .subscribe((siteUser) => {
        if (siteUser) {
          this.init();
        }
      });
  }

  public handleUsageChanged(equipmentUsage: EquipmentUsage): void {
    this.assetInfo.updateEquipmentUsage(equipmentUsage);
  }

  public missingPostModelDescription(): boolean {
    this.checkAssetTypeMisc();
    let response: boolean = this.assetInfo.isAssetTypeMisc;
    if ( this.assetInfo.isAssetTypeMisc ) {
      if ( this.assetInfo.postModelDescription ) {
        response = this.assetInfo.isAssetTypeMisc && this.assetInfo.postModelDescription.length <= 0;
      }
    }
    return response;
  }

  public checkAssetTypeMisc() {
    if ( this.assetInfo.typeId ) {
      const selectedEquipmentType: EquipmentType =  this.equipmentTypeList.filter(type => type.id === this.assetInfo.typeId )[0];
      if ( selectedEquipmentType ) {
        this.assetInfo.isAssetTypeMisc = selectedEquipmentType.name.startsWith(':');
      }
    }
  }

  public loadDropDownLists() {
    this.loadManufacturerList();
    this.loadModelList();
    this.loadCategoryList();
    this.loadTypeList();
  }

  public loadPreSetDropDownLists() {
    this.loadManufacturerList(this.assetInfo.modelId, this.assetInfo.categoryId, this.assetInfo.typeId );
    this.loadModelList(this.assetInfo.manufacturerId, this.assetInfo.categoryId, this.assetInfo.typeId);
    this.loadCategoryList(this.assetInfo.manufacturerId, this.assetInfo.modelId, this.assetInfo.typeId);
    this.loadTypeList(this.assetInfo.manufacturerId, this.assetInfo.modelId, this.assetInfo.categoryId);
  }

  public getAssetInfoTypeObjectValue(): EquipmentType {
    return EquipmentType.constructEquipmentType(this.assetInfo.typeId, this.assetInfo.typeName);
  }

  public getAssetInfoCategoryObjectValue(): EquipmentCategory {
    return EquipmentCategory.constructEquipmentCategory(this.assetInfo.categoryId, this.assetInfo.categoryName);
  }

  public getAssetInfoModelObjectValue(): EquipmentModel {
    return EquipmentModel.constructEquipmentModel(this.assetInfo.modelId, this.assetInfo.modelName);
  }

  public getAssetInfoManufacturerObjectValue(): EquipmentManufacturer {
    return EquipmentManufacturer.constructEquipmentManufacturer(this.assetInfo.manufacturerId, this.assetInfo.manufacturerName);
  }

  public syncManufacturerList(disableAutoSelectManufacturer? : boolean): void {
    if ( !this.assetInfo.manufacturerId ) {
      this.autoSelectManufacturer = disableAutoSelectManufacturer? false : true;
      this.loadManufacturerList(this.assetInfo.modelId , this.assetInfo.categoryId, this.assetInfo.typeId );
    } else {
      this.verifyCurrentManufacturerValue();
    }
  }

  public verifyCurrentManufacturerValue(): void {
    const model: number = ( this.assetInfo.modelId ) ? this.assetInfo.modelId : 0;
    const category: number = ( this.assetInfo.categoryId ) ? this.assetInfo.categoryId : 0;
    const type: number = ( this.assetInfo.typeId ) ? this.assetInfo.typeId : 0;
    this.addAssetService.getManufacturerList( model , category , type).subscribe(manufacturers => {
      const currentManufacturer = manufacturers.filter(manufacturer => manufacturer.manufacturerId === this.assetInfo.manufacturerId );
      if ( !currentManufacturer || !currentManufacturer[0]) {
        this.equipmentManufacturerList = manufacturers;
        this.assetInfo.manufacturerId = 0;
        this.syncModelList();
        this.syncCategoryList();
        this.syncTypeList();
      }
      this.subscribeManufacturerAutoCompleteChanges();
    });
  }

  public syncModelList(disableAutoSelectModel? : boolean): void {
    if ( !this.assetInfo.modelId ) {
      this.autoSelectModel = !disableAutoSelectModel;
      this.loadModelList( this.assetInfo.manufacturerId, this.assetInfo.categoryId, this.assetInfo.typeId );
    } else {
      this.verifyCurrentModelValue();
    }
  }

  public verifyCurrentModelValue(): void {
    const make: number = ( this.assetInfo.manufacturerId ) ? this.assetInfo.manufacturerId : 0;
    const category: number = ( this.assetInfo.categoryId ) ? this.assetInfo.categoryId : 0;
    const type: number = ( this.assetInfo.typeId ) ? this.assetInfo.typeId : 0;
    this.addAssetService.getModelList(make, category, type).subscribe(models => {
      const currentModel = models.filter( model => model.modelId === this.assetInfo.modelId );
      if ( !currentModel || !currentModel[0] ) {
        this.equipmentModelList = models;
        this.assetInfo.modelId = 0;
        this.syncManufacturerList();
        this.syncCategoryList();
        this.syncTypeList();
      }
      this.subscribeModelAutoCompleteChanges();
    });
  }

  public syncCategoryList(): void {
    if ( !this.assetInfo.categoryId ) {
      this.autoSelectCategory  = true;
      this.loadCategoryList( this.assetInfo.manufacturerId, this.assetInfo.modelId , this.assetInfo.typeId );
    } else {
      this.verifyCategoryValue();
    }
  }

  public verifyCategoryValue(): void {
    const make: number = 0;
    const model: number = 0;
    const type: number = ( this.assetInfo.typeId ) ? this.assetInfo.typeId : 0;
    this.addAssetService.getCategoryList( make, model , type).subscribe(categories => {
      const currentCategory = categories.filter(category => category.categoryId === this.assetInfo.categoryId );
      if ( !currentCategory || !currentCategory[0] ) {
        this.equipmentCategoryList = categories;
        this.assetInfo.categoryId = 0;
        this.assetInfo.categoryName = '';
        this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
        this.generateDescription();
        this.syncManufacturerList();
        this.syncModelList();
        this.syncTypeList();
      }
      this.subscribeCategoryAutoCompleteChanges();
    });
  }

  public syncTypeList(): void {
    if ( !this.assetInfo.typeId ) {
      this.autoSelectType = true;
      this.loadTypeList( this.assetInfo.manufacturerId, this.assetInfo.modelId , this.assetInfo.categoryId);
    } else {
      this.verifyTypeValue();
    }
  }

  public verifyTypeValue(): void {
    const make: number = 0;
    const model: number = 0;
    const category: number = ( this.assetInfo.categoryId ) ? this.assetInfo.categoryId : 0;
    this.addAssetService.getTypesList( make, model , category).subscribe(types => {
      const currentType = types.filter(type => type.id === this.assetInfo.typeId );
      if ( !currentType || !currentType[0] ) {
        this.equipmentTypeList = types;
        this.assetInfo.typeId = 0;
        this.assetInfo.typeName = '';
        this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
        this.generateDescription();
        this.syncManufacturerList();
        this.syncModelList();
        this.syncCategoryList();
      }
      this.checkAssetTypeMisc();
      this.subscribeTypeAutoCompleteChanges();
    });
  }

  editAsset() {
    if ( this.assetInfo ) {
      this.toggleEditAssetFlag();
      this.loadPreSetDropDownLists();
    }
  }
  public editTranslatedDescr() {
    if (this.assetInfo) {
      this.bAddTranslatedDesc = true;
      this.editAsset();
    }
  }

  /* Used by unsaved changes detector */
  hasChanges(): boolean {
    return this.assetInfo.editAssetInformation;
  }

  public broadcastEditModeChanged(isOnEditMode: boolean) {
    this.editModeChanged.emit(isOnEditMode);
  }

  public removeDuplicatedCategoryItems(items : EquipmentCategory[] , source : EquipmentCategory[] ) : EquipmentCategory[] {
    let response : EquipmentCategory[] = source;
    for ( let item of items ){
      response = response.filter(element => element.categoryName != item.categoryName);
    }
    return response;
  }

  public removeDuplicatedTypeItems(items : EquipmentType[] , source : EquipmentType[] ) : EquipmentType[] {
    let response : EquipmentType[] = source;
    for ( let item of items ){
      response = response.filter(element => element.name != item.name);
    }
    return response;
  }

  public isShootPriceValid(): boolean {
    if ( this.assetSaleInfo.shootPrice > 0 && this.assetSaleInfo.sellerReserveAmt > 1.2 * this.assetSaleInfo.shootPrice ) {
      return false;
    } else {
      return true;
    }
  }

  public showShootPrice(): boolean {
    let canEditShootPrice = PermissionUtils.hasPermission(this.userSessionService, permissionType.canUpdateAssetSalesInfo);
    if (this.assetSaleInfo && (this.assetSaleInfo.saleType === RBA_LIVE || this.assetSaleInfo.saleType === null)) {
      canEditShootPrice = canEditShootPrice && true;
    } else {
      canEditShootPrice = false;
    }
    return canEditShootPrice;
  }

  private cleanTypeAndCategoryFields(): void{
    this.assetInfo.categoryId = undefined;
    this.assetInfo.categoryName = '';
    this.categoryControl.setValue(this.getAssetInfoCategoryObjectValue(), {onlySelf: true, emitEvent: false});
    this.assetInfo.typeId = undefined;
    this.assetInfo.typeName = '';
    this.typeControl.setValue(this.getAssetInfoTypeObjectValue(), {onlySelf: true, emitEvent: false});
  }

  public getCurrencyPlaceHolder(): string {
    return getCurrencyCodeAndSymbol(this.assetSaleInfo?.opportunityCurrency);
  }
  public isCurrencyPrefixVisible(theValue: any): boolean {
    return theValue != null && theValue !== '';
  }

}
